// src/components/viewer/CheckBreakdown.jsx

import React from 'react';

const CheckBreakdown = ({ data }) => {
    const totalFound = (data.totalUsers || 0) - (data.totalNotFound || 0);
    const riskType = (data.riskTypeGroup || 'CRP').toUpperCase();

    // Determine which checks apply based on risk type
    const showActivity = riskType === 'HRP' || riskType === 'CRP';
    const showJobLevel = riskType === 'CRP';
    const showSearchActivity = riskType === 'CRP';

    // Derive each check's numerator from the SAME per-user data the detail table
    // uses (tableData), so the summary band can never contradict the table.
    // Previously the band used aggregate counts (e.g. noBasePICount); when that
    // aggregate was stored as 0 while per-user rows said "No", the band showed a
    // false "N/N have access". Fall back to the aggregate counts only when
    // per-user rows are unavailable (older records that predate tableData).
    const rows = Array.isArray(data.tableData) ? data.tableData : [];
    const hasRows = rows.length > 0;

    const activePass = hasRows
        ? rows.filter(r => r.inactivity !== 'Inactive').length
        : totalFound - (data.inactiveCount || 0);
    const basePiPass = hasRows
        ? rows.filter(r => r.basePIAccess === 'Yes').length
        : totalFound - (data.noBasePICount || 0);
    const jobLevelPass = hasRows
        ? rows.filter(r => parseInt(r.jobLevel, 10) >= 3).length
        : totalFound - (data.lowJobLevelCount || 0);

    // Derive fail flags for pass/warning styling from the same source.
    const activeFail = hasRows ? (totalFound - activePass) : (data.inactiveCount || 0);
    const basePiFail = hasRows ? (totalFound - basePiPass) : (data.noBasePICount || 0);
    const jobLevelFail = hasRows ? (totalFound - jobLevelPass) : (data.lowJobLevelCount || 0);

    // Search count summary
    const searchSummary = data.searchCountSummary || {};
    const searchPass = searchSummary.passCount || 0;
    const searchBelow = searchSummary.belowThresholdCount || 0;
    const searchNoData = searchSummary.noDataCount || 0;
    const searchTotal = searchSummary.totalEvaluated || totalFound || 0;

    const getSearchCheckClass = () => {
        if (searchTotal === 0) return 'warning';
        if (searchBelow === 0 && searchNoData === 0) return 'pass';
        if (searchBelow > 0) return 'warning';
        return 'pass';
    };

    let checkNum = 0;

    return (
        <div className="check-breakdown">
            <div className="section-title">Validation Results ({riskType})</div>
            
            {showActivity && (
                <div className={`check-item ${activeFail === 0 ? 'pass' : 'fail'}`}>
                    <span className="check-name">{++checkNum}️⃣ Activity Check (≤30 days inactive)</span>
                    <span className="check-result">
                        {activePass}/{totalFound} passed
                    </span>
                </div>
            )}
            
            <div className={`check-item ${basePiFail === 0 ? 'pass' : 'warning'}`}>
                <span className="check-name">{++checkNum}️⃣ Base PI Access</span>
                <span className="check-result">
                    {basePiPass}/{totalFound} have access
                </span>
            </div>
            
            {showJobLevel && (
                <div className={`check-item ${jobLevelFail === 0 ? 'pass' : 'warning'}`}>
                    <span className="check-name">{++checkNum}️⃣ Job Level (&gt;3 for UFS)</span>
                    <span className="check-result">
                        {jobLevelPass}/{totalFound} eligible
                    </span>
                </div>
            )}

            {showSearchActivity && (
                <div className={`check-item ${getSearchCheckClass()}`}>
                    <span className="check-name">{++checkNum}️⃣ Search Activity (informational)</span>
                    <span className="check-result">
                        {searchTotal > 0 ? (
                            <>
                                {searchPass > 0 && (
                                    <span style={{ color: '#1a7f37', marginRight: '6px' }}>
                                        {searchPass} sufficient
                                    </span>
                                )}
                                {searchBelow > 0 && (
                                    <span style={{ color: '#d4880f', marginRight: '6px' }}>
                                        {searchBelow} low
                                    </span>
                                )}
                                {searchNoData > 0 && (
                                    <span style={{ color: '#666' }}>
                                        {searchNoData} no data
                                    </span>
                                )}
                            </>
                        ) : (
                            <span style={{ color: '#666' }}>No data available</span>
                        )}
                    </span>
                </div>
            )}
        </div>
    );
};

export default CheckBreakdown;